import { Text, View } from 'react-native';
import { Glyph, SPINNER_DOTS, TICK } from '~/components/decision';
import type { JobStatus } from '~/lib/api';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * A stage marker's four states.
 *
 * `held` is not a variant of `active`. Nothing is computing while the job waits, so it
 * takes its own colour and glyph rather than the brand dot that means work is under way.
 *
 * The FIELD app never says why it is waiting. Review is an internal step and naming it to
 * an operator standing at a customer's car invites the question the operator cannot
 * answer — so the word here is "Processing", and only the reviewer console says review.
 */
export type StageState = 'done' | 'active' | 'held' | 'pending';

export function stageState(index: number, current: number, isReviewStage: boolean): StageState {
  if (current > index) return 'done';
  if (current !== index) return 'pending';
  return isReviewStage ? 'held' : 'active';
}

/**
 * The word for each stage, in one table.
 *
 * Plain English, from the frames — not the API's own values, which is what the job
 * timeline used to print. It lives here because TWO screens draw this sequence, the wait
 * and the job detail, and an operator who meets "Enriching" on one and "Checking the
 * vehicle" on the other has to work out that they are the same thing.
 *
 * `complete` is the released verdict; `partial` lights the same dot and says the same
 * word, because from the outside it is the same event.
 */
export const STAGE_LABEL: Partial<Record<JobStatus, string>> = {
  queued: 'Queued',
  ingesting: 'Receiving photos',
  analysing: 'Analysing',
  enriching: 'Checking the vehicle',
  pending_review: 'Processing',
  complete: 'Verdict released',
  partial: 'Verdict released',
  failed: 'Failed',
};

/** 24 pt dot. Shape and glyph carry the state, so it survives being washed out. */
export function StageDot({ state }: { state: StageState }) {
  const { c, radius } = useTheme();
  const ring =
    state === 'held'
      ? c.stateInfo
      : state === 'done'
        ? c.text
        : state === 'active'
          ? c.brandPrimary
          : c.border2;
  const fill = state === 'held' ? c.stateInfoBg : state === 'done' ? c.text : c.surface;

  return (
    <View
      style={{
        width: 24,
        height: 24,
        borderRadius: radius.pill,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: ring,
        backgroundColor: fill,
      }}
    >
      {/* Not a person: the field app does not say who, or that there is a who. */}
      {state === 'held' ? <Glyph path={SPINNER_DOTS} size={14} color={c.stateInfo} /> : null}
      {state === 'done' ? <Glyph path={TICK} size={14} color={c.onPrimary} /> : null}
      {state === 'active' ? (
        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: c.brandPrimary }} />
      ) : null}
    </View>
  );
}

/** The ink for a stage's label, so the two timelines cannot drift apart. */
export function useStageInk(state: StageState): string {
  const { c } = useTheme();
  if (state === 'held') return c.stateInfo;
  return state === 'pending' ? c.dim : c.text;
}

/**
 * One row of a vertical timeline: dot, optional connector down to the next row, label and
 * an optional note.
 */
export function StageRow({
  state,
  label,
  note,
  time,
  last,
}: {
  state: StageState;
  label: string;
  note?: string;
  /**
   * The clock for this stage, when one exists.
   *
   * Frames 07 and 09 both ask for it and Phase 17 could not draw it — there were no
   * per-stage timestamps, only `created_at` and a heartbeated `updated_at`, so four
   * numbers would have meant inventing three. Migration 0024 writes six.
   *
   * Undefined draws NOTHING, and that is deliberate: absence means the stage was not
   * reached or predates 0024, and a dash would read as a duration of none.
   */
  time?: string | null;
  /** No connector below the final row. */
  last?: boolean;
}) {
  const { c, t, space } = useTheme();
  const ink = useStageInk(state);
  // The stage the job is ON is the one being read. Weight says which without a second
  // colour, which matters on a screen held in the sun.
  const weight = state === 'done' || state === 'pending' ? '400' : '600';
  return (
    <View style={{ flexDirection: 'row', alignItems: 'stretch', gap: space.sm }}>
      <View style={{ alignItems: 'center', width: 24 }}>
        <StageDot state={state} />
        {last ? null : (
          <View
            style={{
              flex: 1,
              width: 2,
              minHeight: space.sm,
              backgroundColor: state === 'done' ? c.text : c.border2,
            }}
          />
        )}
      </View>
      <View style={{ flex: 1, paddingBottom: last ? 0 : space.md }}>
        <Text style={[t.label, { color: ink, fontWeight: weight }]}>{label}</Text>
        {note ? <Text style={[t.body, { color: c.dim }]}>{note}</Text> : null}
      </View>
      {time ? (
        // Tabular, so six clocks read as a column rather than as six separate facts.
        <Text style={[t.monoValue, { fontSize: 12, lineHeight: 24, color: c.dim }]}>{time}</Text>
      ) : null}
    </View>
  );
}

/**
 * The five stages a job passes through before a verdict exists, as segments.
 *
 * Four machine stages — queued, ingesting, analysing, enriching — and a fifth for held
 * for review. It is the same truth the waiting timeline tells, at card size: filled from
 * the job's own status and nothing else.
 *
 * There is no percentage in it, and that is the point. The API exposes no per-stage
 * progress, so a continuous bar could only be drawn from a number nobody measured —
 * decoration presented as measurement. A segment is either reached or it is not, which is
 * a thing the status actually says.
 */
export const BAR_STAGES: readonly JobStatus[] = [
  'queued',
  'ingesting',
  'analysing',
  'enriching',
  'pending_review',
];

/** -1 when the job is past the bar (terminal) or never on it (failed). */
export function barIndexFor(status: JobStatus): number {
  return BAR_STAGES.indexOf(status);
}

export function StageBar({ status }: { status: JobStatus }) {
  const { c, radius, space } = useTheme();
  const current = barIndexFor(status);
  // Terminal and failed jobs are not on the bar at all: the decision pill or the Failed
  // pill is the whole story by then, and a bar under it would invite a second reading.
  if (current === -1) return null;

  return (
    <View
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 1, max: BAR_STAGES.length, now: current + 1 }}
      // Named, because five unlabelled bars say nothing to anything that cannot see them.
      accessibilityLabel={`Stage ${current + 1} of ${BAR_STAGES.length}`}
      style={{ flexDirection: 'row', gap: space.xxs }}
    >
      {BAR_STAGES.map((stage, i) => {
        const state = stageState(i, current, stage === 'pending_review');
        const fill =
          state === 'held'
            ? c.stateInfo
            : state === 'done'
              ? c.primary
              : state === 'active'
                ? c.brandPrimary
                : c.s3;
        return (
          <View
            key={stage}
            testID={`stage-seg-${stage}`}
            style={{ flex: 1, height: 4, borderRadius: radius.sm, backgroundColor: fill }}
          />
        );
      })}
    </View>
  );
}
